import { AI_MODE, AI_STATUS, AI_STATUS_LABELS, canDisableAI } from "./aiInterviewApi";

// "Interviewer" on the schedule and edit forms. Only the two AI choices call the AI Interviewer service.
export const INTERVIEWER = {
  PANELIST: "panelist",
  AI_INTERVIEWER: AI_MODE.AI_INTERVIEWER,
  PANELIST_WITH_AI: AI_MODE.PANELIST_WITH_AI,
};

export const INTERVIEWER_CHOICES = [
  {
    value: INTERVIEWER.PANELIST,
    label: "Human interviewer",
  },
  {
    value: INTERVIEWER.AI_INTERVIEWER,
    label: "AI interviewer",
  },
  {
    value: INTERVIEWER.PANELIST_WITH_AI,
    label: "Human + AI assistant",
  },
];

export const AI_MIN_SLOT_MINUTES = 15;
export const AI_MAX_SLOT_MINUTES = 120;
// Enable at least this long before the start: the plan takes ~1 min and the bot joins 90 s early
export const AI_MIN_LEAD_MINUTES = 5;

export const isAIChoice = (value) => value === INTERVIEWER.AI_INTERVIEWER || value === INTERVIEWER.PANELIST_WITH_AI;

// SiftHire's `ai_mode` for a choice ("ai_interviewer", "panelist_with_ai" or null), and back
export const aiModeOf = (choice) => (isAIChoice(choice) ? choice : null);
export const interviewerOfMode = (aiMode) => (isAIChoice(aiMode) ? aiMode : INTERVIEWER.PANELIST);

// Only the AI interviewer can run an interview with no panelists
export const panelistsRequired = (choice) => choice !== INTERVIEWER.AI_INTERVIEWER;
export const PANELIST_REQUIRED = "Select at least one panelist";
export const PANELIST_OPTIONAL_HINT = "Optional. The AI conducts this interview.";

// Interviewer choice for an existing AI session (null when AI was never enabled)
export const interviewerOfSession = (session) => {
  if (!session || session.status === AI_STATUS.CANCELLED) return INTERVIEWER.PANELIST;
  return session.config?.mode === AI_MODE.PANELIST_WITH_AI ? INTERVIEWER.PANELIST_WITH_AI : INTERVIEWER.AI_INTERVIEWER;
};

// "google", "microsoft", "other" (a link the AI can't join) or null (no link and no calendar)
export const meetingProviderOf = ({ link, calendarProvider } = {}) => {
  const url = (link || "").trim();
  if (url) {
    if (/(^|\/\/|\.)meet\.google\.com/i.test(url)) return "google";
    if (/(^|\/\/|\.)teams\.(microsoft|live)\.com/i.test(url)) return "microsoft";
    if (/^https?:\/\//i.test(url)) return "other";
  }
  // No link yet: the connected calendar creates a Meet or Teams link when it books the slot
  if (calendarProvider === "google" || calendarProvider === "microsoft") return calendarProvider;
  return null;
};

// Meeting fields on a raw SiftHire interview (calendar.join_link, or google.meet_link on older interviews)
export const meetingOfInterview = (interview) => {
  const location = typeof interview?.location === "string" ? interview.location : "";
  return {
    link: interview?.meeting_link || interview?.calendar?.join_link || interview?.google?.meet_link || location || "",
    calendarProvider: interview?.calendar_provider || null,
  };
};

const minutesBetween = (start, end) => Math.round((end.getTime() - start.getTime()) / 60000);

export const AI_GOOGLE_MEET_ONLY = "AI interviewer is available for Google Meet only";

// Why the AI can't join this kind of meeting, or "" when it can. Google Meet only for now.
export const aiMeetingReason = (meetingProvider) => {
  if (!meetingProvider) return "The AI needs a Google Meet link. Pick Google Meet as the meet type.";
  if (meetingProvider !== "google") return AI_GOOGLE_MEET_ONLY;
  return "";
};

// Why the AI can't run in these slots ([{ start: Date, end: Date }]), or "" when it can
export const aiSlotReason = ({ slots = [], now = Date.now() }) => {
  if (!slots.length) return "Pick a time first.";
  for (const { start, end } of slots) {
    if (!(start instanceof Date) || !(end instanceof Date) || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "Pick a time first.";
    }
    const minutes = minutesBetween(start, end);
    if (minutes < AI_MIN_SLOT_MINUTES || minutes > AI_MAX_SLOT_MINUTES) {
      return `The AI needs a ${AI_MIN_SLOT_MINUTES}–${AI_MAX_SLOT_MINUTES} minute slot${minutes > 0 ? ` (this one is ${minutes} min)` : ""}.`;
    }
    if (start.getTime() - now < AI_MIN_LEAD_MINUTES * 60000) {
      return `AI must be turned on at least ${AI_MIN_LEAD_MINUTES} minutes before the start.`;
    }
  }
  return "";
};

// Why the AI can't be turned on for these slots ([{ start: Date, end: Date }]), or "" when it can
export const aiBlockReason = ({ slots = [], meetingProvider, now = Date.now() }) =>
  aiMeetingReason(meetingProvider) || aiSlotReason({ slots, now });

// { [choice]: { disabled, reason } } for the three options.
// `initial` is the interview's current choice and `sessionStatus` its AI session status (edit form).
export const interviewerOptionStates = ({ initial = INTERVIEWER.PANELIST, sessionStatus = null, blockReason = "" }) => {
  const states = {};
  const cancelled = sessionStatus === AI_STATUS.CANCELLED;
  // AI is on and the bot has joined or the interview started: nothing can change
  const locked = isAIChoice(initial) && Boolean(sessionStatus) && !canDisableAI(sessionStatus);
  const lockedReason = locked
    ? `AI can't be changed now (${AI_STATUS_LABELS[sessionStatus] || sessionStatus}).`
    : "";

  INTERVIEWER_CHOICES.forEach(({ value }) => {
    let reason = "";
    if (value === initial) reason = "";
    else if (locked) reason = lockedReason;
    else if (isAIChoice(value)) {
      if (cancelled) reason = "AI was turned off for this interview and can't be turned back on.";
      else if (isAIChoice(initial)) reason = "The AI mode can't be changed once AI is on. Choose Panelist to turn AI off.";
      else reason = blockReason;
    }
    states[value] = { disabled: Boolean(reason), reason };
  });
  return states;
};

// What saving the form does on the AI service: "enable", "disable" or "" (nothing)
export const interviewerChange = (initial, selected) => {
  if (selected === initial) return "";
  if (isAIChoice(selected)) return isAIChoice(initial) ? "" : "enable";
  return isAIChoice(initial) ? "disable" : "";
};
